import { apiClient } from '../client';
import type { ApiResponse } from '../types';

/**
 * What a tenant still owes before the application is usable.
 *
 * The gate reads exactly one field — `blocked` — because the server decides
 * what "done" means for each kind of tenant and the client only enforces it.
 * Putting the rule in both places is how the wizard and the endpoint come to
 * disagree about whether somebody may leave.
 */
export interface OnboardingStep {
  /**
   * PROFILE | EQUIPMENT | SUBMIT | APPROVAL | REJECTED.
   *
   * The last two are not things the applicant can act on. They are the wait,
   * and the refusal — reported as outstanding because that is exactly what
   * they are: reasons the account is not usable yet.
   */
  step: string;
  /** Shown as-is; written to be actionable rather than "profile incomplete". */
  reason: string;
  /** Field names still empty, when `step` is PROFILE. */
  missingFields: string[];
}

export interface OnboardingStatus {
  /** CUSTOMER | TOWPROVIDER | NONE — NONE meaning onboarding does not apply. */
  tenantKind: string;
  tenantId: number;
  companyName: string | null;
  /** DRAFT | SUBMITTED | APPROVED | REJECTED, or null before a profile row exists. */
  profileStatus: string | null;
  /** Nothing outstanding. Drives the banner. */
  complete: boolean;
  /**
   * Must be held at the wizard. The one thing the gate tests.
   *
   * Not `!complete`: an established tenant who has gone incomplete keeps
   * working and gets a banner, because being evicted from your own portal
   * over an unpriced service reads as the account having been reset.
   */
  blocked: boolean;
  outstanding: OnboardingStep[];
}

/** GET /api/v1/Onboarding/status */
export async function getStatus(): Promise<OnboardingStatus> {
  const response = await apiClient.get<ApiResponse<OnboardingStatus>>(
    '/api/v1/Onboarding/status',
  );
  return response.data.data;
}

/**
 * POST /api/v1/Onboarding/submit
 *
 * Returns the recomputed status, so the caller does not have to re-fetch to
 * discover whether the submission actually released the gate.
 */
export async function submit(): Promise<OnboardingStatus> {
  const response = await apiClient.post<ApiResponse<OnboardingStatus>>(
    '/api/v1/Onboarding/submit',
  );
  return response.data.data;
}

/** One company waiting to be let in, as an administrator sees it. */
export interface Application {
  tenantKind: string;
  tenantId: number;
  companyName: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  city: string | null;
  state: string | null;
  reviewStatus: string;
  submittedDate: string | null;
  reviewedDate: string | null;
  reviewNotes: string | null;
  /** Trucks listed. Zero is worth seeing before approving a tow provider. */
  equipmentCount: number;
}

/**
 * GET /api/v1/Onboarding/applications
 *
 * Administrator-only. Defaults to those awaiting a decision; pass `ALL` for
 * the history.
 */
export async function getApplications(reviewStatus?: string): Promise<Application[]> {
  const response = await apiClient.get<ApiResponse<Application[]>>(
    '/api/v1/Onboarding/applications',
    { params: reviewStatus === undefined ? undefined : { reviewStatus } },
  );
  return response.data.data ?? [];
}

/**
 * POST /api/v1/Onboarding/review
 *
 * Administrator-only, and the door: the gate tests for APPROVED and nothing
 * else, so nobody gets in until this is called with `approve`.
 */
export async function review(payload: {
  tenantKind: string;
  tenantId: number;
  approve: boolean;
  reviewNotes: string | null;
}): Promise<boolean> {
  const response = await apiClient.post<ApiResponse<boolean>>(
    '/api/v1/Onboarding/review',
    payload,
  );
  return response.data.data;
}

/** What self-serve registration asks for. */
export interface SignupRequest {
  /** CUSTOMER or TOWPROVIDER. Nothing else may self-register. */
  tenantKind: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string | null;
  password: string;
}

export interface SignupResult {
  userId: number;
  tenantId: number;
  tenantKind: string;
}

/**
 * POST /api/v1/Onboarding/signup — unauthenticated.
 *
 * Creates the company and its first login. The account it produces is
 * deliberately held at the onboarding wizard afterwards, which is the same
 * place an admin-created account lands.
 */
export async function signup(request: SignupRequest): Promise<SignupResult> {
  const response = await apiClient.post<ApiResponse<SignupResult>>(
    '/api/v1/Onboarding/signup',
    request,
  );
  return response.data.data;
}
